import { useCallback, useRef, useState } from 'react';

import { toMedicalFieldPayload } from '../utils';
import { MEDICAL_FORM_SCHEMA } from '../validationSchema';

import type { SaveField } from '../../../../../common';
import type { MedicalFormValues } from '../types';
import type { SaveMedicalFieldParams } from './types';

export default function useSaveMedicalField({ formik, onUpdate, disabled }: SaveMedicalFieldParams) {
  const savedValues = useRef(formik.initialValues);
  const queue = useRef(Promise.resolve());
  const [failedFields, setFailedFields] = useState<Array<keyof MedicalFormValues>>([]);

  const saveField: SaveField<MedicalFormValues> = useCallback((name, value) => {
    if (disabled) return;
    const values = value === undefined ? { ...formik.values } : { ...formik.values, [name]: value };
    void formik.setFieldTouched(name, true, true);

    const save = queue.current.catch(() => undefined).then(async () => {
      const data = toMedicalFieldPayload(name, values);
      try {
        await MEDICAL_FORM_SCHEMA.validateAt(name, values);
      } catch {
        return;
      }
      if (data[name] === toMedicalFieldPayload(name, savedValues.current)[name]) {
        setFailedFields((fields) => fields.filter((field) => field !== name));
        return;
      }
      if (await onUpdate(data)) {
        savedValues.current = { ...savedValues.current, [name]: values[name] };
        setFailedFields((fields) => fields.filter((field) => field !== name));
      } else {
        setFailedFields((fields) => [...fields.filter((field) => field !== name), name]);
      }
    });
    queue.current = save;
    return save;
  }, [disabled, formik, onUpdate]);

  const retry = useCallback(async () => {
    for (const name of failedFields) {
      await saveField(name);
    }
  }, [failedFields, saveField]);

  return { saveField, hasSaveError: failedFields.length > 0, retry };
}
